import { useState, useEffect } from "react";
import useIsMobile from "../../hooks/useIsMobile";
import CloseIcon from "@mui/icons-material/Close";
import { anatomyConstants } from "../../constants/anatomyConstants";

const UploadDICOMModal = ({
  files = [],
  progress = [],
  uploading,
  success,
  onClose,
  onUpload,
}) => {
  const isMobile = useIsMobile();
  const [localFiles, setLocalFiles] = useState(files);

  // sync prop changes
  useEffect(() => {
    setLocalFiles(files);
  }, [files]);

  // auto-close if no files left
  useEffect(() => {
    if (!localFiles.length) {
      onClose();
    }
  }, [localFiles, onClose]);

  const handleRemoveFile = (index) => {
    if (uploading) return;
    setLocalFiles((fs) => fs.filter((_, i) => i !== index));
  };

  const handleUploadClick = () => {
    onUpload({ files: localFiles });
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div
        className={`bg-white p-6 rounded shadow-lg overflow-y-auto ${
          isMobile ? "w-full max-w-sm mx-4" : "w-1/3"
        }`}
      >
        <h2 className="text-xl font-semibold mb-4 text-[var(--main-color)]">
          Upload DICOM Files
        </h2>

        <div className="space-y-4 max-h-[50vh] overflow-y-auto">
          <div className="mb-2">
            <p className="text-sm text-gray-600">
              Ready to upload your files. Please make sure you upload a zip file
              that contains dicom files.
            </p>
          </div>

          {localFiles.map((file, i) => (
            <div key={i} className="mb-4">
              <div className="flex items-center mb-1">
                <button
                  onClick={() => handleRemoveFile(i)}
                  disabled={uploading}
                  className="text-red-500 hover:text-red-700 mr-2"
                  title="Remove file"
                >
                  <CloseIcon />
                </button>
                <p className="font-medium text-gray-700">
                  <strong>{file.name}</strong>
                </p>
              </div>

              {progress[i] > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-[var(--main-color)] h-2.5 rounded-full"
                    style={{ width: `${progress[i]}%` }}
                  />
                </div>
              )}

              {uploading && progress[i] > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {`Uploading… ${progress[i].toFixed(1)}%`}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          {!uploading && (
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleUploadClick}
            disabled={uploading || localFiles.length === 0}
            className="bg-[var(--main-color)] text-white px-4 py-2 rounded hover:bg-[var(--main-color-bold)]"
          >
            Upload
          </button>
        </div>

        {success && (
          <div className="mt-4 text-green-500 text-center">
            Upload Successful!
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadDICOMModal;
